import type { ExpandedTags } from 'exifreader';

import { buildSegments, type Point, type RectGeometry } from './screencastGeometry';
import type {
  Area,
  BaseArea,
  Comment,
  ParseScreencastTooltipsResult,
  PolylineArea,
  TooltipSyntax,
  ValueDistribution,
} from './types';

const DEFAULT_MIN_FRACTION_DIGITS = 0;
const DEFAULT_MAX_FRACTION_DIGITS = 1;
const MAX_FRACTION_DIGITS = 20;

type JsonObject = Record<string, unknown>;

export function extractScreencastTooltipsChunk(tags: ExpandedTags): string | null {
  const tag = tags?.pngText?.ScreencastTooltips ?? tags?.png?.ScreencastTooltips;
  if (!tag) {
    return null;
  }

  if (typeof tag.description === 'string' && tag.description.length > 0) {
    return tag.description;
  }

  if (typeof tag.value === 'string' && tag.value.length > 0) {
    return tag.value;
  }

  return null;
}

export function parseScreencastTooltips(raw: string | null | undefined): ParseScreencastTooltipsResult {
  const errors: string[] = [];

  if (!raw || raw.trim().length === 0) {
    return {
      data: null,
      errors: ['ScreencastTooltips payload is empty'],
    };
  }

  let parsedJson: unknown;
  try {
    parsedJson = JSON.parse(raw);
  } catch {
    return {
      data: null,
      errors: ['ScreencastTooltips payload is not valid JSON'],
    };
  }

  if (!isJsonObject(parsedJson)) {
    return {
      data: null,
      errors: ['ScreencastTooltips root must be an object'],
    };
  }

  const version = asInteger(parsedJson.version);
  if (version === null) {
    return {
      data: null,
      errors: ['ScreencastTooltips.version must be an integer'],
    };
  }

  const areas = parseAreas(parsedJson.areas, 'areas', errors);
  const comments = parseComments(parsedJson.comments, 'comments', errors);

  return {
    data: {
      version,
      areas,
      comments,
    },
    errors,
  };
}

function parseAreas(value: unknown, path: string, errors: string[]): Area[] {
  if (value === undefined) {
    return [];
  }

  if (!Array.isArray(value)) {
    errors.push(`${path} must be an array`);
    return [];
  }

  const result: Area[] = [];
  value.forEach((area, index) => {
    const parsedArea = parseArea(area, `${path}[${index}]`, errors);
    if (parsedArea) {
      result.push(parsedArea);
    }
  });
  return result;
}

function parseArea(value: unknown, path: string, errors: string[]): Area | null {
  if (!isJsonObject(value)) {
    errors.push(`${path} must be an object`);
    return null;
  }

  const base: BaseArea = {
    text: asString(value.text),
    syntax: asSyntax(value.syntax),
    highlight: value.highlight === true,
    areas: parseAreas(value.areas, `${path}.areas`, errors),
  };

  const rect = parseRect(value.rect, `${path}.rect`, errors);
  if (rect) {
    return {
      ...base,
      kind: 'rect',
      rect,
    };
  }

  const polyline = parsePolyline(value, path, errors);
  if (polyline) {
    return {
      ...base,
      ...polyline,
      kind: 'polyline',
    };
  }

  errors.push(`${path} must define either rect or polyline geometry`);
  return null;
}

function parseRect(value: unknown, path: string, errors: string[]): RectGeometry | null {
  if (value === undefined) {
    return null;
  }

  if (!Array.isArray(value) || value.length !== 4) {
    errors.push(`${path} must contain [xmin, ymin, xmax, ymax]`);
    return null;
  }

  const rectValues: number[] = [];
  value.forEach((item, index) => {
    const parsed = asInteger(item);
    if (parsed === null || parsed < 0) {
      errors.push(`${path}[${index}] must be a non-negative integer`);
      return;
    }
    rectValues.push(parsed);
  });

  if (rectValues.length !== 4) {
    return null;
  }

  const [xmin, ymin, xmax, ymax] = rectValues;
  if (xmax < xmin || ymax < ymin) {
    errors.push(`${path} has invalid bounds`);
    return null;
  }

  return [xmin, ymin, xmax, ymax];
}

function parsePolyline(
  value: JsonObject,
  path: string,
  errors: string[],
): Pick<PolylineArea, 'radius' | 'points' | 'values' | 'segments'> | null {
  const radius = asInteger(value.radius);
  if (radius === null || radius < 1) {
    return null;
  }

  const points = parsePoints(value.points, `${path}.points`, errors);
  if (points.length < 2) {
    errors.push(`${path}.points must contain at least 2 points for polyline`);
    return null;
  }

  const segments = buildSegments(points);
  const values = parseValueDistributions(value.values, `${path}.values`, errors, points.length);

  return {
    radius,
    points,
    values,
    segments,
  };
}

function parsePoints(value: unknown, path: string, errors: string[]): Point[] {
  if (!Array.isArray(value)) {
    errors.push(`${path} must be an array`);
    return [];
  }

  const points: Point[] = [];
  value.forEach((point, index) => {
    if (!Array.isArray(point) || point.length !== 2) {
      errors.push(`${path}[${index}] must contain [x, y]`);
      return;
    }

    const x = asInteger(point[0]);
    const y = asInteger(point[1]);
    if (x === null || y === null) {
      errors.push(`${path}[${index}] must contain integers`);
      return;
    }

    points.push([x, y]);
  });

  return points;
}

function parseValueDistributions(
  value: unknown,
  path: string,
  errors: string[],
  pointCount: number,
): ValueDistribution[] {
  if (value === undefined) {
    return [];
  }

  if (!Array.isArray(value)) {
    errors.push(`${path} must be an array`);
    return [];
  }

  const result: ValueDistribution[] = [];
  value.forEach((distribution, index) => {
    const parsed = parseValueDistribution(distribution, `${path}[${index}]`, errors, pointCount);
    if (!parsed) {
      return;
    }

    result.push(parsed);
  });

  return result;
}

function parseValueDistribution(
  value: unknown,
  path: string,
  errors: string[],
  pointCount: number,
): ValueDistribution | null {
  if (!isJsonObject(value)) {
    errors.push(`${path} must be an object`);
    return null;
  }

  const values = parseNumericValuesArray(value.values, `${path}.values`, errors);
  if (values.length === 0) {
    return null;
  }

  const minimumFractionDigits = normalizeFractionDigits(
    value.minimumFractionDigits,
    DEFAULT_MIN_FRACTION_DIGITS,
    `${path}.minimumFractionDigits`,
    errors,
  );
  let maximumFractionDigits = normalizeFractionDigits(
    value.maximumFractionDigits,
    DEFAULT_MAX_FRACTION_DIGITS,
    `${path}.maximumFractionDigits`,
    errors,
  );

  if (maximumFractionDigits < minimumFractionDigits) {
    errors.push(`${path}.maximumFractionDigits must be greater than or equal to minimumFractionDigits`);
    maximumFractionDigits = minimumFractionDigits;
  }

  const canInterpolate = values.length === pointCount && pointCount >= 2;
  if (!canInterpolate) {
    errors.push(`${path}.values length must be equal to points length`);
  }

  return {
    title: asString(value.title),
    unit: asString(value.unit),
    minimumFractionDigits,
    maximumFractionDigits,
    format: asString(value.format),
    values,
    canInterpolate,
  };
}

function parseNumericValuesArray(value: unknown, path: string, errors: string[]): number[] {
  if (!Array.isArray(value)) {
    errors.push(`${path} must be an array`);
    return [];
  }

  const result: number[] = [];
  value.forEach((item, index) => {
    if (typeof item !== 'number' || !Number.isFinite(item)) {
      errors.push(`${path}[${index}] must be a finite number`);
      return;
    }
    result.push(item);
  });
  return result;
}

function normalizeFractionDigits(value: unknown, fallback: number, path: string, errors: string[]): number {
  if (value === undefined) {
    return fallback;
  }

  const parsed = asInteger(value);
  if (parsed === null || parsed < 0 || parsed > MAX_FRACTION_DIGITS) {
    errors.push(`${path} must be an integer in range 0..${MAX_FRACTION_DIGITS}`);
    return fallback;
  }

  return parsed;
}

function parseComments(value: unknown, path: string, errors: string[]): Comment[] {
  if (value === undefined) {
    return [];
  }

  if (!Array.isArray(value)) {
    errors.push(`${path} must be an array`);
    return [];
  }

  const result: Comment[] = [];
  value.forEach((comment, index) => {
    const parsed = parseComment(comment, `${path}[${index}]`, errors);
    if (parsed) {
      result.push(parsed);
    }
  });
  return result;
}

function parseComment(value: unknown, path: string, errors: string[]): Comment | null {
  if (!isJsonObject(value)) {
    errors.push(`${path} must be an object`);
    return null;
  }

  const title = asString(value.title);
  const text = asString(value.text);

  if (!title) {
    errors.push(`${path}.title must be a non-empty string`);
    return null;
  }

  if (!text) {
    errors.push(`${path}.text must be a non-empty string`);
    return null;
  }

  return {
    title,
    text,
    syntax: asSyntax(value.syntax),
  };
}

function asInteger(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isInteger(value)) {
    return null;
  }
  return value;
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

function asSyntax(value: unknown, fallback: TooltipSyntax = 'PLAIN'): TooltipSyntax {
  if (value === 'MARKDOWN') {
    return 'MARKDOWN';
  }

  if (value === 'PLAIN') {
    return 'PLAIN';
  }

  return fallback;
}

function isJsonObject(value: unknown): value is JsonObject {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
